'use client';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import type { PaymentMethod } from '../../appTypes';
import type { DeliveryAddress } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { getEnabledWebsitePaymentModules } from '../../payments/modules';
import {
  getLocalizedEntityField,
  getNormalizedLanguageCode,
} from '../../../miniapp/i18n/localization';
import { getWeightLabel } from '../../../miniapp/utils/weight';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import SavedAddressQuickFill from '../../../miniapp/components/SavedAddressQuickFill';
import { fetchUserAddresses } from '../../api';
import Icon from '../../../miniapp/ui-kit/Icon';
import IconButton from '../../../miniapp/ui-kit/IconButton';

function paymentCardClass(active: boolean): string {
  return active
    ? 'bg-primary border-accent ring-2 ring-accent/10'
    : 'bg-secondary border-transparent hover:bg-surface-high';
}

export default function CartScreen() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const languageCode = getNormalizedLanguageCode(i18n.resolvedLanguage ?? i18n.language);
  const {
    sessionUser,
    cartItems,
    addToCart,
    removeFromCart,
    deliveryAddress,
    deliveryLocation,
    setDeliveryAddress,
    setDeliveryLocation,
    phoneNumber,
    setPhoneNumber,
    saveAddress,
    setSaveAddress,
    paymentMethod,
    setPaymentMethod,
    appConfig,
    checkoutPending,
    submitCheckout,
    clearError,
  } = useWebsiteAppContext();
  const [savedAddresses, setSavedAddresses] = useState<DeliveryAddress[]>([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState<number | null>(null);

  // Guests have no address book; the list is re-fetched whenever a user signs in.
  const visibleSavedAddresses = sessionUser ? savedAddresses : [];

  useEffect(() => {
    if (!sessionUser) {
      return;
    }

    let active = true;
    fetchUserAddresses()
      .then((addresses) => {
        if (!active) {
          return;
        }
        setSavedAddresses(addresses);
        setSaveAddress(addresses.length === 0);
      })
      .catch(() => {
        if (active) {
          setSavedAddresses([]);
          setSaveAddress(true);
        }
      });

    return () => {
      active = false;
    };
  }, [sessionUser, setSaveAddress]);

  const cartItemsCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems],
  );

  const cartSubtotal = useMemo(
    () => cartItems.reduce((total, item) => total + item.subtotal, 0),
    [cartItems],
  );

  const isFreeDelivery = useMemo(() => {
    const threshold = appConfig?.freeDeliveryThreshold ?? 0;
    return threshold > 0 && cartSubtotal >= threshold;
  }, [appConfig?.freeDeliveryThreshold, cartSubtotal]);

  const cartDeliveryCost = useMemo(() => {
    if (cartItems.length === 0 || isFreeDelivery) {
      return 0;
    }
    return appConfig?.deliveryCost ?? 0;
  }, [appConfig?.deliveryCost, cartItems.length, isFreeDelivery]);

  const cartTotal = useMemo(
    () => Number((cartSubtotal + cartDeliveryCost).toFixed(2)),
    [cartDeliveryCost, cartSubtotal],
  );
  const paymentModules = useMemo(() => getEnabledWebsitePaymentModules(appConfig), [appConfig]);

  const removeProductCompletely = (productId: number, weight: number, quantity: number) => {
    for (let index = 0; index < quantity; index += 1) {
      removeFromCart(productId, weight);
    }
  };

  const handlePaymentMethodChange = (value: PaymentMethod) => {
    setPaymentMethod(value);
    clearError();
  };
  const translate = (key: string) => t(key);

  if (cartItems.length === 0) {
    return (
      <div className="rounded-2xl bg-primary border border-border p-10 text-center">
        <h1 className="m-0 text-2xl font-bold text-text">{t('cart.empty')}</h1>
        <p className="m-0 mt-2 text-text-secondary">{t('website.cart.emptyHint')}</p>
        <button
          type="button"
          className="mt-8 border-0 rounded-full px-6 py-3 cursor-pointer bg-accent-gradient text-on-accent font-semibold"
          onClick={() => navigate('/catalog')}
        >
          {t('success.backToCatalog')}
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-12">
      <h1 className="sr-only">{t('website.cart.title')}</h1>
      <div className="lg:col-span-7 space-y-10">
        <section>
          <h2 className="m-0 mb-6 text-xl font-semibold flex items-center gap-2">
            <Icon name="shopping_bag" className="text-accent" />
            {t('cart.selection')}
          </h2>
          <div className="bg-secondary rounded-4xl overflow-hidden p-1">
            <div className="space-y-1">
              {cartItems.map((item) => {
                const imageUrl = item.product.images?.[0]?.url ?? null;
                const productName = getLocalizedEntityField(item.product, 'name', languageCode);
                const categoryName = item.product.category
                  ? getLocalizedEntityField(item.product.category, 'name', languageCode)
                  : t('cart.defaultCategory');

                return (
                  <article
                    key={`${item.product.id}-${item.weight}`}
                    className="bg-primary p-5 md:p-6 flex items-center gap-4 md:gap-6"
                  >
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden bg-secondary shrink-0">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={productName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-text-secondary">
                          <Icon name="image" />
                        </div>
                      )}
                    </div>
                    <div className="grow min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h3 className="m-0 text-base md:text-lg font-semibold text-text truncate">
                            {productName}
                          </h3>
                          <p className="m-0 mt-1 text-[11px] font-bold uppercase tracking-wider text-text-secondary truncate">
                            {categoryName}
                          </p>
                          <p className="m-0 mt-2 text-[11px] font-bold uppercase tracking-wider text-accent">
                            {getWeightLabel(item.product, item.weight, languageCode)}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 text-text-secondary hover:text-error cursor-pointer"
                          onClick={() =>
                            removeProductCompletely(item.product.id, item.weight, item.quantity)
                          }
                          aria-label={t('website.cart.removeProductAria', { name: productName })}
                        >
                          <Icon name="delete" className="text-[20px]" />
                        </button>
                      </div>
                      <div className="flex justify-between items-center mt-4 gap-2">
                        <div className="flex items-center bg-secondary rounded-full px-2 py-1">
                          <IconButton
                            size="xs"
                            iconClassName="text-sm"
                            icon="remove"
                            onClick={() => removeFromCart(item.product.id, item.weight)}
                            label={t('website.cart.decreaseProductAria', { name: productName })}
                          />
                          <span className="w-8 text-center text-sm font-semibold">
                            {item.quantity}
                          </span>
                          <IconButton
                            size="xs"
                            iconClassName="text-sm"
                            icon="add"
                            onClick={() => addToCart(item.product.id, item.weight)}
                            label={t('website.cart.increaseProductAria', { name: productName })}
                          />
                        </div>
                        <p className="m-0 font-bold text-accent">{formatCurrency(item.subtotal)}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <form
          id="website-checkout-form"
          className="space-y-8"
          onSubmit={(event) => void submitCheckout(event)}
        >
          <section className="space-y-6">
            <h2 className="m-0 text-xl font-semibold flex items-center gap-2">
              <Icon name="local_shipping" className="text-accent" />
              Delivery Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {visibleSavedAddresses.length > 0 ? (
                <div className="md:col-span-2">
                  <SavedAddressQuickFill
                    addresses={visibleSavedAddresses}
                    title={t('cart.quickFill')}
                    noneLabel={t('cart.quickFillNone')}
                    selectedAddressId={selectedSavedAddressId}
                    onSelect={(address) => {
                      if (!address) {
                        setSelectedSavedAddressId(null);
                        setDeliveryAddress('');
                        setPhoneNumber('');
                        setDeliveryLocation(null);
                        clearError();
                        return;
                      }
                      setSelectedSavedAddressId(address.id);
                      setDeliveryAddress(address.address);
                      setPhoneNumber(address.phoneNumber ?? '');
                      setDeliveryLocation(address.location);
                      clearError();
                    }}
                  />
                </div>
              ) : null}
              <div className="space-y-2">
                <label
                  htmlFor="phone-number"
                  className="text-[11px] font-bold uppercase tracking-wider text-text-secondary ml-1 block"
                >
                  {t('cart.phoneNumber')}
                </label>
                <PhoneInput
                  country="ge"
                  preferredCountries={['ge', 'tr', 'by', 'ua', 'ru']}
                  enableSearch
                  value={phoneNumber}
                  onChange={(value, _country, _event, formattedValue) => {
                    if (selectedSavedAddressId !== null) {
                      setSelectedSavedAddressId(null);
                    }
                    const normalizedPhone = formattedValue?.trim() || (value ? `+${value}` : '');
                    setPhoneNumber(normalizedPhone);
                    clearError();
                  }}
                  placeholder={t('cart.phonePlaceholder')}
                  specialLabel=""
                  containerClass="!w-full"
                  inputClass="!w-full !h-[52px] !bg-secondary !border-none !rounded-4xl !pl-[72px] !pr-4 !text-text !text-base focus:!bg-primary focus:!shadow-[0_0_0_2px_var(--color-focus-ring)] !transition-all"
                  buttonClass="!border-none !bg-transparent !rounded-l-4xl !w-14"
                  dropdownClass="!w-full !max-w-full !min-w-[160px] !mt-2 !border-none !rounded-4xl !bg-primary !text-text !shadow-ambient"
                  searchClass="!bg-primary"
                  inputProps={{
                    id: 'phone-number',
                    name: 'phone-number',
                    autoComplete: 'tel',
                    required: true,
                  }}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="payment-method"
                  className="text-[11px] font-bold uppercase tracking-wider text-text-secondary ml-1 block"
                >
                  {t('cart.paymentMethod')}
                </label>
                <select
                  id="payment-method"
                  className="w-full h-[52px] bg-secondary border-none rounded-4xl px-4 text-text focus:bg-primary focus:shadow-[0_0_0_2px_var(--color-focus-ring)] focus:outline-none"
                  value={paymentMethod}
                  onChange={(event) =>
                    handlePaymentMethodChange(event.target.value as PaymentMethod)
                  }
                >
                  {paymentModules.map((module) => (
                    <option key={module.code} value={module.code}>
                      {module.getTitle(translate)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2 space-y-2">
                <label
                  htmlFor="delivery-address"
                  className="text-[11px] font-bold uppercase tracking-wider text-text-secondary ml-1 block"
                >
                  {t('cart.deliveryAddress')}
                </label>
                <AddressAutocomplete
                  id="delivery-address"
                  value={deliveryAddress}
                  onChange={(value) => {
                    if (selectedSavedAddressId !== null) {
                      setSelectedSavedAddressId(null);
                    }
                    setDeliveryAddress(value);
                    clearError();
                  }}
                  onLocationChange={setDeliveryLocation}
                  placeholder={t('cart.deliveryPlaceholder')}
                />
                {deliveryAddress.trim() && !deliveryLocation ? (
                  <p className="ml-1 mt-1 text-xs text-warning">{t('cart.selectSuggestionHint')}</p>
                ) : null}
              </div>
              <label className="md:col-span-2 flex items-center gap-3 text-sm text-text-secondary ml-1">
                <input
                  type="checkbox"
                  checked={saveAddress}
                  onChange={(event) => setSaveAddress(event.target.checked)}
                  className="w-4 h-4 accent-accent"
                />
                {t('cart.saveAddress')}
              </label>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="m-0 text-xl font-semibold flex items-center gap-2">
              <Icon name="payments" className="text-accent" />
              {t('cart.paymentMethod')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paymentModules.map((module) => (
                <label
                  key={module.code}
                  className={`relative flex items-center gap-4 p-4 rounded-4xl border-2 cursor-pointer transition-all ${paymentCardClass(paymentMethod === module.code)}`}
                >
                  <input
                    className="sr-only"
                    type="radio"
                    name="payment"
                    value={module.code}
                    checked={paymentMethod === module.code}
                    onChange={() => handlePaymentMethodChange(module.code)}
                  />
                  <div className="w-10 h-10 rounded-circle bg-surface-high flex items-center justify-center text-accent">
                    <Icon name={module.icon} />
                  </div>
                  <div>
                    <p className="m-0 font-semibold text-text">{module.getTitle(translate)}</p>
                    <p className="m-0 mt-0.5 text-xs text-text-secondary">
                      {module.getDescription(translate, appConfig)}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <div className="w-5 h-5 rounded-circle border-2 border-border-solid flex items-center justify-center">
                      <div
                        className={`w-2.5 h-2.5 rounded-circle bg-accent transition-opacity ${paymentMethod === module.code ? 'opacity-100' : 'opacity-0'}`}
                      />
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </section>
        </form>
      </div>

      <aside className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <div className="bg-secondary rounded-2xl p-6 md:p-8 space-y-8">
            <h2 className="m-0 text-xl font-semibold text-text">
              {t('website.cart.orderSummary')}
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center gap-2">
                <span className="text-text-secondary">
                  {t('website.cart.subtotalItems', { count: cartItemsCount })}
                </span>
                <span className="font-semibold text-text">{formatCurrency(cartSubtotal)}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-text-secondary">{t('cart.deliveryFee')}</span>
                <span className="font-semibold text-text">
                  {isFreeDelivery
                    ? t('website.cart.freeFrom', {
                        amount: formatCurrency(appConfig?.freeDeliveryThreshold ?? 0),
                      })
                    : formatCurrency(cartDeliveryCost)}
                </span>
              </div>
              <div className="pt-6 mt-2 border-t border-border">
                <div className="flex justify-between items-end gap-2">
                  <span className="text-lg font-semibold text-text">
                    {t('success.totalAmount')}
                  </span>
                  <p className="m-0 text-3xl font-bold text-accent leading-none">
                    {formatCurrency(cartTotal)}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                form="website-checkout-form"
                className="w-full border-0 bg-accent-gradient text-on-accent py-4 px-6 rounded-full font-semibold text-lg shadow-lg shadow-accent/20 transition-transform active:scale-[0.98] disabled:bg-disabled disabled:text-on-disabled disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                disabled={checkoutPending || cartItems.length === 0}
              >
                {checkoutPending ? t('website.cart.placingOrder') : t('cart.placeOrder')}
                {!checkoutPending ? <Icon name="arrow_forward" /> : null}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
